import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, SLOT_FUND, SLOT_GROUP } from "./_shared.js";

/**
 * `READ-DEBTS` (@UC0269 · US-AIV2-0110 S21 · QĐ PO 26/09 câu F) — "ai nợ ai" GIỮA NGƯỜI VỚI NGƯỜI trong một quỹ, **chỉ trong
 * trợ lý** (không màn Flutter mới). Tier `read`: không phiếu, 0 bút toán.
 *
 * Nguồn DUY NHẤT: `NettingViewService.getNetting` — cùng hàm mà `GET /v1/funds/:id/netting` gọi, VIEW đọc `debt_entries` peer
 * (§3.6 P1.5 "View gợi ý, Action ghi sổ"). Binding không có phép tính nợ thứ hai: số ròng và gợi ý trả lấy nguyên từ đó.
 *
 * 🔴 QUYỀN XEM (QĐ PO): thành viên thường chỉ thấy PHẦN CỦA MÌNH (mọi suất mình đại diện — ghế chính + liên kết sống);
 * chủ nhóm/admin/thủ quỹ thấy toàn quỹ. **Chặt hơn** cửa GUI hiện tại (mọi thành viên xem tất cả) — lọc ở SERVER trong
 * binding, dữ liệu người không được xem không rời API. `roles` vẫn là cả 4 vai: trang mở cho mọi người, PHẠM VI mới theo vai.
 *
 * `status: live` (US-AIV2-0110 S27 · @UC0279 · QĐ người dùng 27/09 "nâng khỏi draft"). Lượt thu 26/09 cho span 85,19% vì model
 * chép TÊN ô tự điền vào ô (`{"slot":"quy","text":"quỹ"}`); sửa GỐC ở biên bằng `dropSlotNameEchoSpans` (domain — cùng hàm cổng
 * thật + bộ chấm eval), KHÔNG ngưỡng riêng: span 100% trên bản ghi đang commit và trên hai lượt thu phụ. Ô `quy` vẫn nhận span
 * RÁC có trong câu ("nợ", "nhóm", "bạn nào" — 12/42 câu bộ chuẩn); S27b (QĐ PO 27/09): ở trang ĐỌC, chữ ô quỹ không khớp mà nhóm
 * có đúng 1 quỹ ⇒ dùng quỹ đó (`soleRefWhenUnmatched`, `resolve-slot.ts`) — câu trả lời in `{tenQuy}` nên người đọc thấy quỹ nào.
 */
export const READ_DEBTS = {
  id: "READ-DEBTS",
  uc: "@UC0269",
  tier: "read",
  status: LIVE,
  roles: ALL_ROLES,
  flags: [],
  // 🔴 US-AIV2-0112 SLOT-GROUP (@UC0300, follow-up H01 #1037): ô nhóm là ĐƯỜNG VÀO khi phiên chưa có nhóm (tab trợ lý toàn cục) — thiếu nó
  // ⇒ `AI_GROUP_REQUIRED` ⇒ người ≥2 nhóm hỏi "mình nợ ai" rơi sang READ-PEOPLE-MINE (probe H01). Phạm vi xem vẫn theo VAI ở nhóm được chốt.
  slots: [SLOT_GROUP, SLOT_FUND],
  llm: {
    description:
      "Xem nợ QUA LẠI GIỮA CÁC THÀNH VIÊN trong quỹ (người này ứng tiền trước, đóng hộ, cho mượn nên người kia còn phải trả lại): mình còn nợ bạn nào bao nhiêu, bạn nào còn nợ tiền mình ứng hay cho mượn, và nên trả cho ai để hết nợ cho gọn. Tiền mình phải đóng VÀO QUỸ hay khoản còn thiếu với quỹ là READ-PEOPLE-MINE; số dư, thu chi của QUỸ là READ-FUND-MONEY; còn nhắc người khác đóng tiền là việc khác, của người quản lý.",
    examples: [
      "ai nợ ai",
      "ai no ai",
      "ai đang nợ ai",
      "ai dang no ai",
      "mình còn nợ bạn nào không",
      "minh con no ban nao khong",
      "ai nợ mình tiền",
      "ai no minh tien",
      "mình đã ứng tiền cho những ai",
      "minh da ung tien cho nhung ai",
      "tiền mình ứng đã ai trả chưa",
      "tien minh ung da ai tra chua",
      "mình đang nợ bạn nào bao nhiêu",
      "minh dang no ban nao bao nhieu",
      "ai còn nợ tiền mình ứng",
      "ai con no tien minh ung",
      "xem ai nợ ai trong quỹ",
      "xem ai no ai trong quy",
      "gộp nợ cho gọn giúp mình",
      "gop no cho gon giup minh",
      "ai trả ai cho hết nợ",
      "ai tra ai cho het no",
      "mình phải trả lại tiền cho ai",
      "minh phai tra lai tien cho ai",
      "còn ai chưa trả lại tiền mình ứng",
      "con ai chua tra lai tien minh ung",
      "nợ qua lại giữa các thành viên thế nào",
      "no qua lai giua cac thanh vien the nao",
      "tôi có nợ bạn nào tiền không",
      "toi co no ban nao tien khong",
      "bạn nào còn nợ tôi",
      "ban nao con no toi",
      "ai mượn tiền mình chưa trả",
      "ai muon tien minh chua tra",
      "mình ứng tiền mua đồ, giờ ai phải trả mình",
      "minh ung tien mua do, gio ai phai tra minh",
      "bảng ai nợ ai của nhóm",
      "bang ai no ai cua nhom",
      "tính xem ai phải trả ai",
      "tinh xem ai phai tra ai",
      "nợ nần giữa mọi người trong nhóm sao rồi",
      "no nan giua moi nguoi trong nhom sao roi",
    ],
  },
  executor: "nettingView.getNetting",
  /**
   * Mảng RỖNG có chủ đích: `apps/mobile` KHÔNG gọi `GET /funds/{id}/netting` (QĐ PO: không màn mới) — khai cửa đó ở đây là
   * khai một cửa mobile không ai gọi (`assert-ai-coverage.sh` đỏ). Trang AI làm được mà mobile chưa có màn = `[]` (xem schema).
   */
  endpoints: [],
  facts: ["tenQuy", "tomTat"],
  templates: {
    reply: "Ai nợ ai trong {tenQuy}: {tomTat}",
  },
  /**
   * rà S21 vòng 2 LOW-A — `READ-PEOPLE-MINE` đứng ĐẦU: mọi câu trả lời kết bằng câu dẫn "tiền phải đóng vào quỹ … hỏi “mình còn phải
   * đóng gì”", nên chip đầu tiên phải là đích của câu dẫn đó (không bắt người dùng gõ lại). `journey` KHÔNG vào prompt / dấu vân tay
   * eval / bộ câu chuẩn (đo 26/09: golden set + prompt của trang giống từng byte trước/sau — runbook S21 §6).
   */
  journey: {
    outcomes: {
      has_debts: { next: ["READ-PEOPLE-MINE", "READ-FUND-MONEY"] },
      // US-AIV2-tourguide-0005 (@UC0364): không còn nợ qua lại ⇒ câu vẫn dẫn sang "mình còn phải đóng gì" (READ-PEOPLE-MINE) — giữ nút đó.
      no_debts: { next: ["READ-PEOPLE-MINE", "READ-FUND-MONEY"] },
    },
  },
  help: { title: "Xem ai nợ ai", tryNow: "ai đang nợ ai" },
} satisfies AiCapabilityPage;
